import React, {useEffect, useState} from "react";
import styled from "styled-components";
import _ from "lodash";
import {Link} from "react-router";
import Switch from "@material-ui/core/Switch";
import CircularProgress from "@material-ui/core/CircularProgress";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import IconButton from "@material-ui/core/IconButton";
import FilterIcon from "@material-ui/icons/FilterList";
import LinkIcon from "@material-ui/icons/Link";

import NumberTextField from "./numeric_text_field";
import Selectors from "common_components/flowmaster/llm_run/selectors";
import Summary from "./summary";
import TestStatus from "./test_status";
import MultiSelectFilter from "./select_filter";
import ValueFilter from "./value_filter";
import StackedInfo from "./stacked_info";

import calculateDuration from "../../utils/calculate_duration";
import renderDuration from "../../utils/render_duration";
import calculateCounts from "../utils/calculate_counts";
import {LlmRunQueryParams} from "../../utils/filtering/get_llm_run_query_params";
import getStatus from "routes/llm_run/utils/get_status";

import {
  LlmRunId,
  LlmRunInfo,
  LlmRunFilterKeys,
} from "common/flowmaster/types/llm_run";
import {OrganisationId} from "common/types/organisation";
import "react-datepicker/dist/react-datepicker.css";
import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import MoreVert from "@material-ui/icons/MoreVert";
import {Button, Tooltip} from "@material-ui/core";
import DateFilter from "common_components/flowmaster/llm_run/date_filter";

const Container = styled.div`
  padding: 0 1em;
  width: calc(100% - 1em);
`;

const Table = styled.table`
  border-collapse: collapse;
  thead th {
    padding-bottom: 0.5em;
  }
  width: 100%;
  table-layout: fixed;
  td,
  th {
    width: 5em;
    &:nth-child(1) {
      width: 9em;
    }
    &:nth-child(2) {
      width: 8em;
    }
    &:nth-child(6),
    &:nth-child(7) {
      width: 7em;
    }
    ,
    &:nth-child(9) {
      width: 6em;
    }
  }
`;
const TableRow = styled.tr`
  &:nth-child(even) {
    background-color: #fcfcfc;
  }
  &:nth-child(odd) {
    background-color: #eef;
  }
  &:hover {
    background-color: #ffffdd;
  }
`;
const TableCell = styled.td`
  padding: 1em 0.5em;
  white-space: nowrap;
  border-top: 1px solid #aaa;
  text-align: center;
`;
const BigTableCell = styled(TableCell)`
  white-space: wrap;
  word-break: break-word;
  padding: 0 0.5em;
`;
export const ListLink = styled(Link)`
  color: inherit;
  text-decoration: none;
  pointer: cursor;
`;

const Loading = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.2);
`;

const CompareLink = styled(Link)`
  text-decoration: none;
  color: black;
  &:hover {
    text-decoration: underline #ddd;
  }
  font-size: 1.2em;
  margin: 0 1em;
`;

type Props = {
  isLoading: boolean;
  organisationId: OrganisationId;
  llmRuns: LlmRunInfo[];
  values: LlmRunQueryParams;
  updateValue: (obj: Record<LlmRunFilterKeys, string | number>) => void;
  goToRun: (id: LlmRunId) => void;
  rerunTests: (id: LlmRunId) => void;
  rerunMultipleTests: (ids: LlmRunId[]) => void;
  maxRuns: number;
  setMaxRuns: (maxRuns: number) => void;
  isDeveloperMode: boolean;
  onDeveloperModeToggle: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onRefresh: () => void;
};

export default function llmRunListComponent({
  isLoading,
  organisationId,
  llmRuns,
  goToRun,
  isDeveloperMode,
  onDeveloperModeToggle,
  rerunTests,
  rerunMultipleTests,
  values,
  updateValue,
  maxRuns,
  setMaxRuns,
  onRefresh,
}: Props) {
  const [showCostAnalysis, setShowCostAnalysis] = useState(false);
  const [isCompactView, setIsCompactView] = useState(window.innerWidth < 2100);
  const counts = calculateCounts(llmRuns);
  function rerunAllTests() {
    rerunMultipleTests(llmRuns.map(run => run.id));
  }

  useEffect(() => {
    const debouncedHandleResize = _.debounce(() => {
      setIsCompactView(window.innerWidth < 2100);
    }, 150);

    window.addEventListener("resize", debouncedHandleResize);

    return () => {
      debouncedHandleResize.cancel();
      window.removeEventListener("resize", debouncedHandleResize);
    };
  }, []);

  return (
    <div style={{display: "flex", width: "100%", flexDirection: "column"}}>
      {isLoading && (
        <Loading>
          <CircularProgress />
        </Loading>
      )}
      <Header
        {...{
          llmRuns,
          values: _.omit(values, ["reasons", "status"]),
          updateValue,
          showCostAnalysis,
          setShowCostAnalysis,
          maxRuns,
          setMaxRuns,
          isDeveloperMode,
          onDeveloperModeToggle,
          rerunAllTests,
          onRefresh,
        }}
      />
      <LlmRunTable
        {...{
          organisationId,
          llmRuns,
          goToRun,
          isDeveloperMode,
          rerunTests,
          updateValue,
          showCostAnalysis,
          counts,
          reasons: values.reasons,
          status: values.status,
          minCost: values.min_cost,
          isCompactView,
        }}
      />
    </div>
  );
}

function Header({
  llmRuns,
  values,
  updateValue,
  showCostAnalysis,
  setShowCostAnalysis,
  maxRuns,
  setMaxRuns,
  isDeveloperMode,
  onDeveloperModeToggle,
  rerunAllTests,
  onRefresh,
}) {
  return (
    <div
      style={{
        backgroundColor: "#f6f6f6",
        padding: "0 1em",
        borderBottom: "1px solid #aaa",
        marginBottom: "1em",
      }}
    >
      <div style={{display: "flex", justifyContent: "space-between"}}>
        <h1>LLM Runs</h1>

        <div style={{display: "flex", alignItems: "baseline"}}>
          <DateFilter values={values} updateValue={updateValue} />
          {isDeveloperMode && (
            <Button
              style={{margin: "0 1em"}}
              onClick={() => rerunAllTests()}
              variant="contained"
            >
              Run Tests
            </Button>
          )}
          <FormControlLabel
            control={
              <Switch
                checked={isDeveloperMode}
                onChange={onDeveloperModeToggle}
                color="primary"
              />
            }
            label="Dev mode"
          />
          <FormControlLabel
            control={
              <Switch
                onClick={() => setShowCostAnalysis(!showCostAnalysis)}
              ></Switch>
            }
            label="Show cost analysis"
          />
          <NumberTextField
            value={maxRuns}
            label="Max Results"
            step={100}
            onChange={value => setMaxRuns(value)}
          />
          <CompareLink
            to={location =>
              location.pathname.replace(/\/(list)/, "/compare") +
              location.search
            }
          >
            Compare
          </CompareLink>
          <Button onClick={onRefresh} variant="contained" color="primary">
            Refresh
          </Button>
        </div>
      </div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-end",
          paddingBottom: "1em",
        }}
      >
        <Summary llmRuns={llmRuns} />
        <Selectors values={values} updateValue={updateValue} />
      </div>
    </div>
  );
}

function LlmRunTable({
  organisationId,
  llmRuns,
  goToRun,
  isDeveloperMode,
  rerunTests,
  updateValue,
  showCostAnalysis,
  counts,
  reasons,
  status,
  minCost,
  isCompactView,
}) {
  const [anchorEl, setAnchorEl] = React.useState(null);
  return (
    <Container>
      <Table>
        <thead collapse-columns>
          <tr
            style={{
              position: "sticky",
              top: 0,
              backgroundColor: "#fff",
              zIndex: 1,
            }}
          >
            <th>Id</th>
            <th>User</th>
            <th>Date</th>
            <th>Time</th>
            <th>Duration</th>
            <th>Active Duration</th>
            <th>
              <MultiSelectFilter
                id="reason-filter"
                title="Reason"
                value={reasons ?? []}
                menuItems={[
                  {
                    value: "classify_clausepart",
                    label: "Classify Clausepart",
                  },
                  {value: "document_upload", label: "Upload"},
                  {value: "remote_api", label: "Remote API"},
                  {value: "reprocess_issue", label: "Reprocess"},
                  {value: "child", label: "Child"},
                ]}
                setValue={value => updateValue({reasons: value})}
              />
            </th>
            <th>
              <MultiSelectFilter
                id="status-filter"
                title="Status"
                value={status ?? []}
                menuItems={[
                  {
                    value: "in_progress",
                    label: "In Progress",
                  },
                  {value: "complete", label: "Complete"},
                  {value: "failed", label: "Failed"},
                ]}
                setValue={value => updateValue({status: value})}
              />
            </th>
            <th>Retries</th>
            <th>Children</th>
            <th>
              <ValueFilter
                prefix="$"
                title="Cost"
                value={minCost}
                setValue={value => updateValue({min_cost: value})}
                step={minCost ? Math.max(0.0001, minCost / 2.0) : 0.01}
              />
            </th>
            {isDeveloperMode && <th>Tests</th>}
            {showCostAnalysis && (
              <>
                <th>Tokens</th>
                <th>Max Toks / Action</th>
                <th>Doc pages</th>
                <th>Doc words</th>
                <th>Doc chars</th>
              </>
            )}
            {!isCompactView && (
              <>
                <th style={{width: "20%"}}>
                  Workflow Name ({counts.workflow})
                </th>
                <th style={{width: "20%"}}>Proj Name ({counts.project})</th>
                <th style={{width: "20%"}}>Doc Name ({counts.document})</th>
                <th style={{width: "40%"}}>
                  Extra info (
                  <span style={{display: "inline-flex", gap: "0.5em"}}>
                    {Boolean(counts.issue) && (
                      <span>Issues: {counts.issue}</span>
                    )}
                    {Boolean(counts.clausepart) && (
                      <span>Clauseparts: {counts.clausepart}</span>
                    )}
                  </span>
                  )
                </th>
              </>
            )}
            {isCompactView && (
              <col style={{width: "300px", minWidth: "300px"}} />
            )}
            {isDeveloperMode && <th style={{width: "2em"}} />}
          </tr>
        </thead>
        <tbody>
          {renderLlmRuns(
            organisationId,
            llmRuns,
            goToRun,
            isDeveloperMode,
            rerunTests,
            updateValue,
            showCostAnalysis,
            anchorEl,
            setAnchorEl,
            isCompactView,
          )}
        </tbody>
      </Table>
    </Container>
  );
}

function renderLlmRuns(
  organisationId,
  llmRuns,
  goToRun,
  isDeveloperMode,
  rerunTests,
  updateValue,
  showCostAnalysis,
  anchorEl,
  setAnchorEl,
  isCompactView,
) {
  return llmRuns.map(llmRun =>
    renderLlmRun(
      organisationId,
      llmRun,
      goToRun,
      isDeveloperMode,
      () => rerunTests(llmRun.id),
      updateValue,
      showCostAnalysis,
      anchorEl,
      setAnchorEl,
      isCompactView,
    ),
  );
}

const InfoSpan = styled.span`
  font-style: italic;
  color: #888;
  text-decoration: dotted underline #888;
`;

function formatTime(date) {
  return new Intl.DateTimeFormat("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).format(date);
}

function renderLlmRun(
  organisationId,
  llmRun,
  goToRun,
  isDeveloperMode,
  rerunTests,
  updateValue,
  showCostAnalysis,
  anchorEl,
  setAnchorEl,
  isCompactView,
) {
  const handleMenuClick = event => {
    setAnchorEl({[llmRun.id]: event.currentTarget});
  };
  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const redirectToDetails = loc =>
    loc.pathname.replace("list", `${llmRun.id}/detail`);
  const filterChildren = () => updateValue({parent: llmRun.run_id});
  const filterBatch = () => updateValue({batch: llmRun.batch_id});
  const filterUser = () => updateValue({user: llmRun.username});
  const workflowUrl = `/organisation/${organisationId}/workflow/${llmRun.workflow_id}/detail`;
  const projectUrl = `/organisation/${organisationId}/project/${llmRun.project_id}/document/list`;
  const documentUrl = `/organisation/${organisationId}/project/${llmRun.project_id}/document/${llmRun.document_id}/detail`;
  const issueUrl = `/organisation/${organisationId}/issue/${llmRun.issue_id}?document=${llmRun.document_id}&project=${llmRun.project_id}&open_issue_list=true`;

  const wordTokens = llmRun.doc_words * 1.3;
  const charTokens = llmRun.doc_chars / 4;
  const avgTokens = (wordTokens + charTokens) / 2;

  return (
    <TableRow key={llmRun.id}>
      <TableCell
        style={{
          cursor: "pointer",
          position: "relative",
        }}
      >
        <>
          <ListLink to={redirectToDetails}>
            {llmRun.id}
            {llmRun.batch_id && (
              <div
                style={{
                  fontSize: "0.8em",
                  color: "grey",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  paddingRight: "24px",
                }}
              >
                {llmRun.batch_id}
              </div>
            )}
          </ListLink>
          {llmRun.batch_id && (
            <IconButton
              size="small"
              onClick={filterBatch}
              style={{
                position: "absolute",
                right: "4px",
                top: "50%",
                transform: "translateY(-50%)",
              }}
            >
              <FilterIcon fontSize="small" />
            </IconButton>
          )}
        </>
      </TableCell>
      <TableCell style={{cursor: "pointer"}}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Tooltip title={llmRun.username}>
            <ListLink
              to={redirectToDetails}
              style={{textOverflow: "ellipsis", overflow: "hidden"}}
            >
              {llmRun.username}
            </ListLink>
          </Tooltip>
          <IconButton size="small" onClick={filterUser}>
            <FilterIcon fontSize="small" />
          </IconButton>
        </div>
      </TableCell>
      <TableCell style={{cursor: "pointer"}}>
        <ListLink to={redirectToDetails}>
          {new Date(llmRun.creation_date).toLocaleDateString()}
        </ListLink>
      </TableCell>
      <TableCell style={{cursor: "pointer"}}>
        <ListLink to={redirectToDetails}>
          {formatTime(new Date(llmRun.creation_date))}
        </ListLink>
      </TableCell>
      <TableCell style={{cursor: "pointer"}}>
        <ListLink to={redirectToDetails}>
          {renderDuration(
            calculateDuration(
              llmRun.creation_date,
              llmRun.last_run ?? llmRun.creation_date,
            ),
          )}{" "}
        </ListLink>
      </TableCell>
      <TableCell style={{cursor: "pointer"}}>
        <ListLink to={redirectToDetails}>
          {renderDuration(llmRun.active_duration)}
        </ListLink>
      </TableCell>
      <TableCell style={{cursor: "pointer", textTransform: "capitalize"}}>
        <ListLink to={redirectToDetails}>
          {renderReason(llmRun.creation_reason, llmRun.is_rerun)}
        </ListLink>
      </TableCell>
      <TableCell style={{cursor: "pointer"}}>
        <ListLink to={redirectToDetails}>{getStatus(llmRun)}</ListLink>
      </TableCell>
      <TableCell style={{cursor: "pointer", width: "2em"}}>
        <ListLink to={redirectToDetails}>{llmRun.retries}</ListLink>
      </TableCell>
      <TableCell style={{cursor: "pointer", width: "3em"}}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <ListLink to={redirectToDetails}>
            {llmRun.child_run_count_by_state?.complete ?? 0} /{" "}
            {llmRun.child_run_count}
            {llmRun.child_run_count_by_state?.failed
              ? ` - ${llmRun.child_run_count_by_state?.failed} F`
              : ""}
          </ListLink>
          <IconButton size="small" onClick={filterChildren}>
            <FilterIcon fontSize="small" />
          </IconButton>
        </div>
      </TableCell>
      <TableCell style={{borderLeft: "1px solid #aaa"}}>
        <Tooltip
          title={
            <div>
              <div>Parent cost: ${(llmRun.parent_cost ?? 0).toFixed(4)}</div>
              <div>Child cost: ${(llmRun.child_cost ?? 0).toFixed(4)}</div>
              <div></div>
            </div>
          }
        >
          <div>${(llmRun.cost ?? 0).toFixed(4)}</div>
        </Tooltip>
      </TableCell>
      {isDeveloperMode && (
        <TableCell style={{borderLeft: "1px solid #aaa", padding: 0}}>
          <div style={{display: "flex", flexDirection: "column"}}>
            <div>
              <TestStatus stats={llmRun.stats} />
            </div>
          </div>
        </TableCell>
      )}
      {showCostAnalysis && (
        <>
          <TableCell>
            {llmRun.total_tokens ?? 0} (
            <InfoSpan title="% of doc tokens">
              {(((llmRun.total_tokens ?? 0) / avgTokens) * 100).toFixed(1)}%
            </InfoSpan>
            )
          </TableCell>
          <TableCell>
            {llmRun.max_tokens ?? 0} (
            <InfoSpan title="% of doc tokens">
              {(((llmRun.max_tokens ?? 0) / avgTokens) * 100).toFixed(1)}%
            </InfoSpan>
            )
          </TableCell>
          <TableCell>{llmRun.doc_pages}</TableCell>
          <TableCell>
            {(llmRun.doc_words ?? 0).toLocaleString()} (
            <InfoSpan title="est Tokens">
              {(Math.round(wordTokens) ?? 0).toLocaleString()} T
            </InfoSpan>
            )
          </TableCell>
          <TableCell>
            {(llmRun.doc_chars ?? 0).toLocaleString()} (
            <InfoSpan title="est Tokens">
              {(Math.round(charTokens) ?? 0).toLocaleString()} T
            </InfoSpan>
            )
          </TableCell>
        </>
      )}
      {!isCompactView ? (
        <>
          <BigTableCell style={{borderLeft: "1px solid #aaa"}}>
            <FilterableName
              name={llmRun.workflow_name}
              url={workflowUrl}
              filter={() => updateValue({workflow: llmRun.workflow_id})}
            />
          </BigTableCell>
          <BigTableCell>
            <FilterableName
              name={llmRun.project_name}
              url={projectUrl}
              filter={() => updateValue({project: llmRun.project_id})}
            />
          </BigTableCell>
          <BigTableCell>
            <FilterableName
              name={llmRun.document_name}
              url={documentUrl}
              filter={() => updateValue({document: llmRun.document_id})}
            />
          </BigTableCell>
          <BigTableCell>
            {llmRun.issue_name && (
              <FilterableName
                label="Issue"
                name={llmRun.issue_name}
                url={issueUrl}
                filter={() => updateValue({issue: llmRun.issue_id})}
              />
            )}
            {llmRun.clausepart_reference && (
              <FilterableName
                label="Clausepart"
                name={llmRun.clausepart_reference}
                url={documentUrl}
                filter={() => updateValue({clausepart: llmRun.clausepart_id})}
              />
            )}
            {llmRun.clause_reference && (
              <FilterableName
                label="Clause"
                name={`${llmRun.section_reference} - ${llmRun.clause_reference}`}
              />
            )}
            {llmRun.definition_term && (
              <FilterableName
                label="Definition"
                name={`${llmRun.definition_term}`}
              />
            )}
          </BigTableCell>
        </>
      ) : (
        <TableCell
          style={{
            borderLeft: "1px solid #aaa",
            padding: ".5rem",
            verticalAlign: "top",
            width: "100%",
          }}
        >
          <div style={{display: "flex", flexDirection: "column"}}>
            <StackedInfo
              label="Workflow"
              value={llmRun.workflow_name}
              url={workflowUrl}
              filter={() => updateValue({workflow: llmRun.workflow_id})}
            />

            <StackedInfo
              label="Project"
              value={llmRun.project_name}
              url={projectUrl}
              filter={() => updateValue({project: llmRun.project_id})}
            />

            <StackedInfo
              label="Document"
              value={llmRun.document_name}
              url={documentUrl}
              filter={() => updateValue({document: llmRun.document_id})}
            />

            {llmRun.issue_name && (
              <StackedInfo
                label="Issue"
                value={llmRun.issue_name}
                url={issueUrl}
                filter={() => updateValue({issue: llmRun.issue_id})}
              />
            )}

            {llmRun.clausepart_reference && (
              <StackedInfo
                label="Clausepart"
                value={llmRun.clausepart_reference}
                url={documentUrl}
                filter={() => updateValue({clausepart: llmRun.clausepart_id})}
              />
            )}

            {llmRun.clause_reference && (
              <StackedInfo
                label="Clause"
                value={`${llmRun.section_reference} - ${llmRun.clause_reference}`}
              />
            )}

            {llmRun.definition_term && (
              <StackedInfo label="Definition" value={llmRun.definition_term} />
            )}
          </div>
        </TableCell>
      )}
      {isDeveloperMode && (
        <TableCell
          style={{
            width: "40px",
            minWidth: "40px",
            padding: "0 4px",
            textAlign: "center",
          }}
        >
          <IconButton size="small" onClick={handleMenuClick}>
            <MoreVert />
          </IconButton>
          <Menu
            anchorEl={anchorEl?.[llmRun.id]}
            keepMounted
            open={Boolean(anchorEl?.[llmRun.id])}
            onClose={handleMenuClose}
          >
            <MenuItem
              onClick={() => {
                rerunTests();
                handleMenuClose();
              }}
            >
              Rerun Tests
            </MenuItem>
          </Menu>
        </TableCell>
      )}
    </TableRow>
  );
}

function renderReason(reason, isRerun) {
  const reasonMap = {
    reprocess_issue: "Reprocess",
    document_upload: "Upload",
    remote_api: "Remote",
    classify_clausepart: "Classify CP",
    reclassify_clausepart: "Reclassify CP",
  };

  const displayReason = reasonMap[reason] || reason;
  return isRerun ? `${displayReason} (R)` : displayReason;
}

interface FilterableNameProps {
  label?: string;
  name: string;
  url?: string;
  filter?: () => void;
}

function FilterableName({label, name, url, filter}: FilterableNameProps) {
  return (
    <div>
      {label && (
        <div
          style={{
            textAlign: "left",
            fontStyle: "italic",
            fontSize: "0.75em",
            color: "#888",
          }}
        >
          {label}
        </div>
      )}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div style={{textAlign: "left"}}>{name}</div>
        <div>
          {filter ? (
            <IconButton
              size="small"
              onClick={() => filter()}
              style={{paddingTop: 0, paddingBottom: 0}}
            >
              <FilterIcon />
            </IconButton>
          ) : null}
          {url ? (
            <ListLink to={url}>
              <IconButton
                size="small"
                style={{paddingTop: 0, paddingBottom: 0}}
              >
                <LinkIcon />
              </IconButton>
            </ListLink>
          ) : null}
        </div>
      </div>
    </div>
  );
}
